import { useEffect } from "react";
import { IoClose } from "react-icons/io5";
import "./TopDrawer.css";

/**
 * TopDrawer
 * A responsive panel that slides DOWN from the top of the screen.
 *
 * Props:
 *  - open            : boolean  -> controls visibility
 *  - onClose         : function -> called on backdrop click / Esc / close button
 *  - title           : string   -> header text
 *  - children        : ReactNode-> the body content (user-specific info goes here)
 *  - closeOnBackdrop : boolean  -> click outside to close (default true)
 *  - width           : string   -> optional max width (default "640px")
 */
const TopDrawer = ({
  open,
  onClose,
  title,
  children,
  closeOnBackdrop = true,
  width = "640px",
}) => {
  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`top-drawer-backdrop ${open ? "open" : ""}`}
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        className={`top-drawer ${open ? "open" : ""}`}
        style={{ maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <div className="top-drawer-header">
          <h3 className="top-drawer-title">{title}</h3>
          <button
            type="button"
            className="top-drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className="top-drawer-body">{children}</div>
      </div>
    </>
  );
};

export default TopDrawer;
